// utils/scrollRestoration.js
//
// Хуудас хооронд шилжих үед scroll байршлыг хадгалж, browser back/forward
// товч дарахад өмнөх хуудасны scroll байршил руу буцаана.
//
// Хэрэглээ:
//   1. _app.js-д useScrollRestoration(pendingRestoreRef) дуудна
//   2. Data fetch хийдэг хуудас `notifyScrollReady()`-г дата ачаалагдсаны
//      дараа дуудна (context/ScrollRestoreContext/index.js-ээс авна).
//      Ингэснээр зураг, жагсаалт ачаалагдаагүй байхад scroll буцаахыг
//      түр зогсоож, дата бэлэн болсны дараа scroll хийнэ.
//
// Data татдаггүй энгийн static хуудас бол notifyScrollReady дуудах
// шаардлагагүй — 50ms-ийн дараа fallback scroll хийгдэнэ.

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

export default function useScrollRestoration(pendingRestoreRef) {
    const router = useRouter();

    // URL → scrollY map. useRef тул re-render-т алга болохгүй.
    const positionsRef = useRef({});

    // popstate (back/forward) товч даргдсан эсэхийг тэмдэглэх flag
    const shouldRestoreRef = useRef(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!('scrollRestoration' in window.history)) return;

        // Browser-ын автомат scroll restoration-ыг унтрааж, өөрсдөө
        // удирдана — SPA-д хэрэгтэй зан үйлийг бүрэн хянах зорилготой.
        window.history.scrollRestoration = 'manual';

        // Хуучин хуудсын scroll байршлыг хадгалах.
        const saveCurrentScroll = () => {
            const url = router.asPath;
            positionsRef.current[url] = window.scrollY;
        };

        // Өгөгдсөн URL-ийн scroll байршилд буцаах.
        const doScrollRestore = (targetUrl) => {
            const y = positionsRef.current[targetUrl] || 0;
            window.scrollTo(0, y);
        };

        // Хуудас орхих үед (tab хаах, reload г.м.)
        const onBeforeUnload = () => {
            saveCurrentScroll();
        };

        // Next.js route солих эхэлсэн үед — одоогийн scroll хадгалах
        const onRouteChangeStart = () => {
            saveCurrentScroll();
        };

        // Route солих дууссан үед
        const onRouteChangeComplete = (url) => {
            if (shouldRestoreRef.current) {
                shouldRestoreRef.current = false;

                // Data татдаг хуудас (getNewsList, getTeachersList гэх мэт)
                // `notifyScrollReady` дуудах хүртэл хүлээнэ. pendingRestoreRef-д
                // callback хадгалж, дуудагдахад scroll буцаана.
                //
                // Хэрэв notifyScrollReady 300ms дотор ирэхгүй бол (static
                // хуудас) timeout-оор fallback scroll хийнэ.
                let done = false;

                const restoreNow = () => {
                    if (done) return;
                    done = true;
                    doScrollRestore(url);
                };

                if (pendingRestoreRef) {
                    pendingRestoreRef.current = restoreNow;
                }

                // Fallback — 300ms дотор notifyScrollReady дуудагдаагүй бол
                // scroll-г яагаад ч гэсэн буцаана (static хуудасны хувьд)
                setTimeout(() => {
                    if (!done) {
                        if (pendingRestoreRef) pendingRestoreRef.current = null;
                        restoreNow();
                    }
                }, 300);
            } else {
                // Шинэ navigation — Link, push г.м.
                window.scrollTo(0, 0);
            }
        };

        // browser back/forward товч дарсан үед popstate event сэргэнэ.
        // Дараах routeChangeComplete-д restore хийхийг тэмдэглэнэ.
        const onPopState = () => {
            shouldRestoreRef.current = true;
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('popstate', onPopState);
        router.events.on('routeChangeStart', onRouteChangeStart);
        router.events.on('routeChangeComplete', onRouteChangeComplete);

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('popstate', onPopState);
            router.events.off('routeChangeStart', onRouteChangeStart);
            router.events.off('routeChangeComplete', onRouteChangeComplete);
        };
    }, [router, pendingRestoreRef]);
}
